import * as ko from 'knockout';

import i18n from '../i18n';
import * as trialsApi from '../api/trials';
import { ListLoaderDelegate } from '../components/list_loader';
import { ListRequestParams } from '../api/request';
import { I18nText, translate } from '../i18n_text';
import { parseDateTime } from '../api/serialization';
import { BarChartConfig } from '../ko_bindings/bar_chart';
import { reactListTablesEnabled } from '../permissions';
import { formatNumberOrDash, timeAgo } from '../utils';
import { GridView, pageParams } from '../utils/grid_view';
import { carriesGridView } from '@/lib/gridViewState';
import {
  TrialActivityStatusListProps,
  TrialActivityStatusPage,
  TrialActivityStatusQuery,
  TrialActivityStatusRow,
  TrialActivityStatusSite,
} from 'react-islands';

let template = require('raw-loader!../../templates/trial_activity_status.html').default;

type Tab = 'status' | 'active_trials';

const INACTIVITY_THRESHOLD_MS = 3 * 7 * 24 * 60 * 60 * 1000;

function isInactive(lastTimestamp: string): boolean {
  const time = parseDateTime(lastTimestamp);
  return !time || new Date().getTime() - time.getTime() > INACTIVITY_THRESHOLD_MS;
}

function toSiteRow(data: trialsApi.SiteActivityStatusData): TrialActivityStatusSite {
  return {
    name: translate(data.name_json),
    collected: formatNumberOrDash(data.collected, 0),
    toCollect: formatNumberOrDash(data.to_collect, 0),
    lastActivity: data.last_timestamp ? timeAgo(data.last_timestamp) : '',
    staffNames: data.staff,
  };
}

class TrialActivityStatusScreen implements ListLoaderDelegate<
  trialsApi.TrialActivityStatusData,
  TrialActivityStatus
> {
  statusChart = ko.observable<BarChartConfig>();
  loadingStatusChart = ko.observable(true);
  selected = ko.observable<Tab>('status');
  newActivityTable = reactListTablesEnabled();

  private view = new GridView('trial-activity-status');

  tableProps = ko.pureComputed<TrialActivityStatusListProps>(() => ({
    view: this.view,
    fetchPage: this.fetchPage,
    fetchSites: this.fetchSites,
  }));

  constructor(params?: { filters?: unknown }) {
    if (this.newActivityTable) {
      this.loadTableView(params ? params.filters : undefined);
    }
    this.loadStatusChart();
  }

  private loadTableView(urlParams: unknown) {
    this.view.load(urlParams);
    if (carriesGridView(urlParams)) {
      this.selected('active_trials');
    }
  }

  private loadStatusChart() {
    this.loadingStatusChart(true);

    trialsApi.activitySummary().then((data) => {
      this.statusChart({
        type: 'bar',
        title: i18n.t('Active Trials')(),
        xTitle: '',
        fontSize: 12,
        labels: [i18n.t('Total active')(), i18n.t('No activity in 3 weeks')()],
        datasets: [
          {
            label: '',
            color_key: '',
            data: [
              { x: 0, y: data.total },
              { x: 1, y: data.inactive },
            ],
          },
        ],
      });
      this.loadingStatusChart(false);
    });
  }

  selectStatus = () => {
    this.selected('status');
    this.loadStatusChart();
  };

  selectActiveTrials = () => {
    this.selected('active_trials');
  };

  fetch(params: ListRequestParams) {
    return trialsApi.listActivityStatus(params);
  }

  instantiate(data: trialsApi.TrialActivityStatusData) {
    return new TrialActivityStatus(data);
  }

  toRow = (status: TrialActivityStatus): TrialActivityStatusRow => ({
    id: status.id,
    name: translate(status.nameJson),
    lastMeasurement: status.lastTimestamp ? timeAgo(status.lastTimestamp) : '',
    inactive: status.inactive,
  });

  remove(id: string) {
    return Promise.resolve(null);
  }

  canRemove(entity: TrialActivityStatus) {
    return false;
  }

  private fetchPage = (query: TrialActivityStatusQuery): Promise<TrialActivityStatusPage> =>
    this.view.fetchPage(
      query,
      (options) => trialsApi.listActivityStatus(pageParams(query, 'name_prefix'), options),
      this
    );

  private fetchSites = (trialId: string): Promise<TrialActivityStatusSite[]> =>
    trialsApi.listSiteActivityStatus(trialId).then((sites) => sites.map(toSiteRow));
}

class TrialActivityStatus {
  id: string;
  nameJson: I18nText;
  lastTimestamp: string;
  inactive: boolean;
  expanded = ko.observable(false);

  constructor(data: trialsApi.TrialActivityStatusData) {
    this.id = data.id;
    this.nameJson = data.name_json;
    this.lastTimestamp = data.last_timestamp;
    this.inactive = isInactive(this.lastTimestamp);
  }

  toggleExpanded = () => {
    this.expanded(!this.expanded());
  };
}

export let trialActivityStatus = {
  name: 'trial-activity-status',
  viewModel: TrialActivityStatusScreen,
  template: template,
};

ko.components.register(trialActivityStatus.name, trialActivityStatus);
